import merge from 'lodash/merge'
import type { ReactNode } from 'react'
import React, { useContext as useContextCore } from 'react'
import type { Components } from './components'
import { components as defaultComponents } from './components'
import { validate as defaultValidate } from './validate'

export type RegisterFormFields = {
  username: string
  fullName: string
  password?: string
  phone?: string
} & Record<string, any>

export type RegisterSuccessArgs = {
  values: RegisterFormFields
  getRecaptchaToken: () => Promise<string | undefined>
}

export type RegisterProviderProps = {
  children?: ReactNode
  components?: Components
  url: string
  onSuccess?: (args: RegisterSuccessArgs) => Promise<void>
  initialValues?: RegisterFormFields
  validate?: (values) => any
  recaptchaSiteKey: string
  includePassword?: boolean
}

export type RegisterContextType = Omit<RegisterProviderProps, 'children'> & {
  isSuccessCaptcha: boolean
  setIsSuccessCaptcha: React.Dispatch<React.SetStateAction<boolean>>
  errorMessage: string | null
  setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>
}

const context = React.createContext<RegisterContextType>({} as RegisterContextType)
const CoreProvider = context.Provider

export const RegisterProvider = ({
  children,
  components,
  onSuccess,
  initialValues,
  validate,
  includePassword = false,
  ...rest
}: RegisterProviderProps) => {
  const [isSuccessCaptcha, setIsSuccessCaptcha] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState(null)
  components = merge({}, defaultComponents, components)
  const _validate = (values) => {
    const defaultErrors = defaultValidate(values)
    const validateErrors = validate?.(values) ?? {}

    return merge({}, defaultErrors, validateErrors)
  }
  initialValues = merge({ username: ``, fullName: '', phone: '' }, initialValues)

  return (
    <CoreProvider
      value={{
        ...rest,
        includePassword,
        isSuccessCaptcha,
        setIsSuccessCaptcha,
        errorMessage,
        setErrorMessage,
        onSuccess,
        initialValues,
        validate: _validate,
        components,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useRegisterContext = () => useContextCore(context)
